/** Back office → Academy → Subscriptions (D-110): every Academy Pass, with a manual extension and
 * a recorded refund (the money goes back through the provider; nothing is deleted). */

import {
  type SubscriptionAdmin,
  useAcademySalesSubscriptionsExtendCreate,
  useAcademySalesSubscriptionsList,
  useAcademySalesSubscriptionsRefundCreate,
} from "@agencyos/api-client";
import { countryName, formatXaf } from "@agencyos/core";
import { Badge, Button, Dialog, GlassCard, SelectField, Skeleton, TextField } from "@agencyos/ui-web";
import { type FormEvent, useState } from "react";
import { useTranslation } from "react-i18next";

import { dateText } from "../../billing/shared";
import { ErrorList, Pager, digits, useAcademyRoles, useRefreshAcademy } from "../shared";

const STATUSES = ["active", "expired", "refunded", "pending"] as const;
const TONE: Record<string, "success" | "default" | "warning" | "danger"> = {
  active: "success",
  pending: "warning",
  refunded: "danger",
  expired: "default",
};

type Action = { kind: "extend" | "refund"; row: SubscriptionAdmin } | null;

export function SubscriptionsTab() {
  const { t, i18n } = useTranslation();
  const { canEditSettings } = useAcademyRoles();
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [action, setAction] = useState<Action>(null);
  const list = useAcademySalesSubscriptionsList({ page, ...(status ? { status } : {}), ...(q ? { q } : {}) });
  const data = list.data?.status === 200 ? list.data.data : undefined;
  return (
    <GlassCard as="section" className="stack">
      <div className="section-title">
        <div>
          <h2>{t("academySales.admin.tab.subscriptions")}</h2>
          <p className="muted">{t("academySales.admin.refundHint")}</p>
        </div>
      </div>
      <div className="row wrap" role="search" aria-label={t("academySales.admin.filters")}>
        <TextField label={t("academySales.admin.search")} value={q} onChange={(e) => (setQ(e.target.value), setPage(1))} />
        <SelectField
          label={t("academySales.admin.status")}
          value={status}
          onChange={(e) => (setStatus(e.target.value), setPage(1))}
          options={[{ value: "", label: t("academySales.admin.all") }, ...STATUSES.map((s) => ({ value: s, label: t(`academySales.account.status.${s}`) }))]}
        />
      </div>
      {data === undefined ? (
        <Skeleton style={{ height: 200 }} />
      ) : data.results.length === 0 ? (
        <p className="muted">{t("academySales.admin.empty")}</p>
      ) : (
        <>
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th scope="col">{t("academySales.admin.learner")}</th>
                  <th scope="col">{t("academySales.admin.period")}</th>
                  <th scope="col">{t("academySales.admin.paid")}</th>
                  <th scope="col">{t("academySales.admin.country")}</th>
                  <th scope="col">{t("academySales.admin.method")}</th>
                  <th scope="col">{t("academySales.admin.status")}</th>
                  {canEditSettings ? <th scope="col">{t("academySales.admin.actions")}</th> : null}
                </tr>
              </thead>
              <tbody>
                {data.results.map((row) => (
                  <tr key={row.id}>
                    <td>
                      <strong>{row.learner_name || row.learner_email}</strong>
                      <div className="muted">{row.learner_email}</div>
                    </td>
                    <td className="nowrap">
                      {row.starts_on && row.ends_on ? `${dateText(row.starts_on, i18n.language)} → ${dateText(row.ends_on, i18n.language)}` : "—"}
                      <div className="muted">
                        {t(`academySales.account.kind.${row.kind}`)}
                        {row.renewal ? ` · ${t("academySales.admin.renewal")}` : ""}
                        {row.extended_days ? ` · +${row.extended_days} j` : ""}
                      </div>
                    </td>
                    <td className="ao-num">
                      {formatXaf(row.amount_xaf)}
                      {row.promo ? <div className="muted">{row.promo}</div> : null}
                      {row.refunded_xaf ? <div className="ko">−{formatXaf(row.refunded_xaf)}</div> : null}
                    </td>
                    <td>{row.country ? countryName(row.country, i18n.language) : "—"}</td>
                    <td>
                      {row.method ? t(`academySales.method.${row.method}`, { defaultValue: row.method }) : "—"}
                      {row.utm_source ? <div className="muted">{row.utm_source}</div> : null}
                    </td>
                    <td>
                      <Badge tone={TONE[row.status] ?? "default"}>{t(`academySales.account.status.${row.status}`)}</Badge>
                    </td>
                    {canEditSettings ? (
                      <td className="nowrap">
                        <Button size="sm" variant="ghost" onClick={() => setAction({ kind: "extend", row })}>
                          {t("academySales.admin.extend")}
                        </Button>
                        {row.kind === "paid" && (row.status === "active" || row.status === "expired") ? (
                          <Button size="sm" variant="ghost" onClick={() => setAction({ kind: "refund", row })}>
                            {t("academySales.admin.refund")}
                          </Button>
                        ) : null}
                      </td>
                    ) : null}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pager page={page} hasNext={Boolean(data.next)} onChange={setPage} />
        </>
      )}
      {action ? <ActionDialog action={action} onClose={() => setAction(null)} /> : null}
    </GlassCard>
  );
}

function ActionDialog({ action, onClose }: { action: NonNullable<Action>; onClose: () => void }) {
  const { t } = useTranslation();
  const refresh = useRefreshAcademy();
  const extend = useAcademySalesSubscriptionsExtendCreate();
  const refund = useAcademySalesSubscriptionsRefundCreate();
  const [value, setValue] = useState(action.kind === "extend" ? "7" : String(action.row.amount_xaf));
  const [note, setNote] = useState("");
  const pending = extend.isPending || refund.isPending;
  const done = () => {
    refresh();
    onClose();
  };
  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (action.kind === "extend") {
      extend.mutate({ id: action.row.id, data: { days: Number(value), note } }, { onSuccess: done });
    } else {
      refund.mutate({ id: action.row.id, data: { amount_xaf: Number(value), note } }, { onSuccess: done });
    }
  };
  return (
    <Dialog
      open
      title={action.kind === "extend" ? t("academySales.admin.extendTitle") : t("academySales.admin.refundTitle")}
      onClose={onClose}
    >
      <form className="stack" onSubmit={onSubmit}>
        {action.kind === "refund" ? <p className="muted">{t("academySales.admin.refundHint")}</p> : null}
        <TextField
          label={action.kind === "extend" ? t("academySales.admin.days") : t("academySales.admin.amount")}
          inputMode="numeric"
          value={value}
          onChange={(e) => setValue(digits(e.target.value))}
          required
        />
        <TextField label={t("academySales.admin.reason")} value={note} onChange={(e) => setNote(e.target.value)} required />
        <ErrorList error={extend.error ?? refund.error} />
        <div className="row">
          <Button type="submit" variant={action.kind === "refund" ? "destructive" : "primary"} disabled={pending || !note.trim() || !value}>
            {t("academySales.admin.confirm")}
          </Button>
          <Button variant="ghost" onClick={onClose}>
            {t("academySales.admin.cancel")}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
